import {ChangeEvent, forwardRef, Ref} from 'react';
import './flat-select-dropdown.scss';
import {
  Autocomplete,
  FormControl,
  Slide,
  SxProps,
  Typography,
  Box,
  Paper,
  TextFieldPropsColorOverrides,
  Avatar,
} from '@mui/material';
import {Theme} from '@mui/material/styles';
import SvgIcon from '@mui/material/SvgIcon';
import CheckIcon from '@mui/icons-material/Check';
import {OverridableStringUnion} from '@mui/types';
import {Pallete} from '../../theme/bluebird';
import FlatInput from '../flat-input/flat-input';
import {Circle} from '@mui/icons-material';
import {translations} from '../../i18n/translations';
import {useTranslation} from 'react-i18next';

export interface Option {
  key: string;
  label: string;
  selectedColor?: string;
  picture?: string;
  colorBlob?: string;
  borderColor?: string;
}

export interface SelectDropdownProps {
  id?: string;
  label?: string;
  required?: boolean;
  errors?: string;
  options: Option[];
  value?: Option;
  placeholder?: string;
  readonly?: boolean;
  onChange: (selectedOption: Option) => void;
  onBlur?: () => void;
  sx?: SxProps<Theme>;
  color?: OverridableStringUnion<
    'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning',
    TextFieldPropsColorOverrides
  >;
  disabled?: boolean;
  disabledTooltip?: string;
  role?: 'user';
  clearable?: boolean;
  loading?: boolean;
  autoFocus?: boolean;
}

const avatarSx: SxProps<Theme> = {
  width: '22px',
  height: '22px',
};

export const FlatSelectDropdown = forwardRef(
  (
    {
      label,
      required,
      options,
      errors,
      sx,
      value,
      onChange,
      placeholder,
      readonly,
      disabled,
      color = 'gray',
      role,
      disabledTooltip,
      clearable,
      autoFocus,
      ...rest
    }: SelectDropdownProps,
    ref: Ref<HTMLInputElement>
  ) => {
    const {t} = useTranslation();
    const hasError = !!errors;

    const handleChange = (event: ChangeEvent<{}>, selectedOption: Option | null) => {
      if (selectedOption) onChange(selectedOption);
      else onChange({key: '', label: ''});
    };

    return (
      <FormControl className="flat-select-dropdown" sx={{width: '100%'}}>
        <Autocomplete
          ref={ref}
          {...rest}
          // @ts-ignore
          value={value?.key ? value : null}
          onChange={handleChange}
          options={options}
          disabled={disabled}
          readOnly={readonly}
          loadingText={t(translations.loading_content.loading)}
          noOptionsText={t(translations.search_select_input.no_options)}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          getOptionLabel={(option) => option.label}
          disableClearable={!clearable}
          renderOption={(props, option, state) => {
            return (
              <li {...props} style={{padding: 0}}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    height: role === 'user' ? '40px' : '28px',
                    padding: '8px 12px',
                    flexDirection: 'row',
                    gap: '8px',
                    alignItems: 'center',
                    color: Pallete.gray.base,
                    fontSize: '14px',
                    fontWeight: 400,
                    '&:hover': {
                      backgroundColor: Pallete.blue.bgseniormenu,
                    },
                  }}
                >
                  {option.colorBlob ? (
                    <Circle sx={{color: option.colorBlob}} />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '4px',
                        alignItems: 'center',
                      }}
                    >
                      {role === 'user' && option && (
                        <Avatar
                          sx={{
                            ...avatarSx,
                            border: option.borderColor && `1.5px solid ${option.borderColor}`,
                          }}
                          src={option.picture}
                        />
                      )}
                      <Typography
                        component="span"
                        noWrap
                        sx={{
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                      >
                        {option.label}
                      </Typography>
                    </Box>
                  )}
                  {state.selected ? (
                    <SvgIcon sx={{fontSize: 'small'}}>
                      <CheckIcon />
                    </SvgIcon>
                  ) : null}
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <Box>
              {role === 'user' && value && (
                <Avatar
                  sx={{
                    ...avatarSx,
                    border: value.borderColor && `1.5px solid ${value.borderColor}`,
                    position: 'absolute',
                    bottom: '10px',
                    left: '12px',
                    zIndex: 2,
                  }}
                  src={value.picture}
                />
              )}
              <FlatInput
                {...params}
                ref={params.InputProps.ref}
                InputLabelProps={{children: null}}
                required={required}
                // errors={errors}
                sx={{
                  '.MuiInputBase-root': {
                    minHeight: '42px',
                  },
                  '.MuiInputBase-input': {
                    webkitTextFillColor: readonly ? Pallete.gray.base : `${value?.selectedColor}`,
                    textFillColor: readonly ? Pallete.gray.base : `${value?.selectedColor}`,
                  },
                  '.MuiFilledInput-root': {
                    fontSize: '14px',
                    fontWeight: 500,
                    height: '42px',
                    width: '100%',
                    padding:
                      role === 'user' && value && value.key !== ''
                        ? '0px 12px 0 42px !important'
                        : '0px 5px 0 12px !important',
                  },
                  '& input': {
                    color: value?.selectedColor,
                  },
                  '& fieldset': {border: 'none'},
                }}
                placeholder={placeholder}
                color={color}
                label={label}
                readonly={readonly}
                disabled={disabled}
                disabledTooltip={disabledTooltip}
                autoFocus={autoFocus}
              />
            </Box>
          )}
          PaperComponent={(props) => (
            // @ts-ignore
            <Paper
              component="div"
              elevation={0}
              {...props}
              sx={{
                borderRadius: '5px!important',
                boxShadow:
                  '0px 1px 2px rgba(60, 64, 67, 0.3), 0px 1px 3px 1px rgba(60, 64, 67, 0.15)',
              }}
            />
          )}
          ListboxProps={{
            sx: {
              '.MuiList-root': {
                borderRadius: '5px',
                padding: '4px 0',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                border: '1px solid orange',
              },
            },
          }}
          sx={{
            minHeight: '42px',
            borderRadius: '5px 5px 0 0',
            '& .MuiFormControl-root': {
              height: '100%',
            },
            '& .MuiInputBase-root': {
              height: '100%',
              py: 0,
              color: Pallete.gray.base,
              fontWeight: '400',
            },
            '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
              px: '12px',
              py: '8px',
            },
            ...sx,
          }}
        />
        <Slide direction="left" in={hasError} mountOnEnter unmountOnExit>
          <span className="input-error" style={{color: Pallete.red.base}}>
            {errors}
          </span>
        </Slide>
      </FormControl>
    );
  }
);
